import React, {useMemo} from 'react'
import {useHistory} from "react-router";
import _ from 'lodash'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

import {usePersistedState} from 'hooks'
import entities from 'reduxStore/entityKeys'

import {BackendJob, HygienistBackendJob} from 'components/EditJob'
import {JobList} from "components/JobList";
import {Filters, SortingOption} from './types'
import {UploadResumeButton} from "components/UploadResumeButton";

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Container,
  StyledArrowDownwardIcon,
  StyledFilterListIcon,
  Title,
  TitleContentButton,
  TitleContentContainer,
  HeaderRow,
  BackButton,
  EmptyState,
  EmptyStateText,
  EmptyStateSubtext,
} from './AvailablePositions.styles'

interface AvailablePositionsProps {
  jobs: HygienistBackendJob[]
  sortBy: SortingOption
  filterBy: Filters
  onSortButtonClick: () => void
  onFilterButtonClick: () => void
  hygienistType?: number;
  onBack?: () => void;
  loading?: boolean;
}

dayjs.extend(utc);

// Compare against the user's local day rather than the current UTC day so the
// 30-day cutoff matches what the hygienist sees on their phone regardless of
// timezone. Parsing the stored YYYY-MM-DD as UTC preserves the calendar day
// the dentist selected.
const isJobExpired = (startDate?: string): boolean => {
  if (!startDate) return false;
  const start = dayjs.utc(startDate);
  if (!start.isValid()) return false;
  const todayLocal = dayjs().startOf('day');
  return todayLocal.diff(start, 'day') > 30;
}

const jobMatchesAvailabilityFilter = (daysOfWeek: string | null | undefined, filterDaysOfWeekDictionary: { [key: number]: boolean }): boolean => {
  if (!daysOfWeek) return false;
  const parsedDaysOfWeek = daysOfWeek.split(',').map(d => +d);
  if (parsedDaysOfWeek.some(value => Number.isNaN(value))) return false;
  return parsedDaysOfWeek.some((status, index) => status === 1 && filterDaysOfWeekDictionary[index]);
}

const getFinalJobList = (jobs: HygienistBackendJob[], sortBy: SortingOption, filterBy: Filters) => {
  const sortValues = {
    [SortingOption.datePosted]: 'createdOn',
    [SortingOption.startDate]: 'startDate',
    [SortingOption.distance]: 'distanceObj.distance.value',
    [SortingOption.applied]: 'isApplied',
  }
  
  // Filter out jobs that are expired (30+ days past start date)
  let jobListFiltered = jobs.filter(job => job.isApplied || !isJobExpired(job.startDate));

  if (filterBy.basis) {
    jobListFiltered = jobListFiltered.filter(({basis, isApplied}) => isApplied || (filterBy.basis && basis === filterBy.basis.id))
  }
  if (filterBy.availability) {
    const filterDaysOfWeekDictionary: { [key: number]: boolean } = filterBy.availability.reduce((acc, {id}) => ({
      ...acc,
      [id]: true
    }), {});
    jobListFiltered = jobListFiltered.filter(({daysOfWeek, isApplied}) => (
      isApplied || jobMatchesAvailabilityFilter(daysOfWeek, filterDaysOfWeekDictionary)
    ))
  }

  let jobListSorted;
  if (sortBy === SortingOption.datePosted || sortBy === SortingOption.applied) {
    jobListSorted = _.sortBy(jobListFiltered, [sortValues[sortBy]]).reverse();
  } else {
    jobListSorted = _.sortBy(jobListFiltered, [sortValues[sortBy]]);
  }

  return jobListSorted
}

const AvailablePositions = ({
                              jobs,
                              filterBy,
                              sortBy,
                              onSortButtonClick,
                              onFilterButtonClick,
                              hygienistType,
                              onBack,
                              loading = false
                            }: AvailablePositionsProps) => {
  const history = useHistory();

  const hygienistUser: any = usePersistedState({
    entityName: entities.user
  })[0]

  const finalJobList = useMemo(() => getFinalJobList(jobs, sortBy, filterBy), [jobs, sortBy, filterBy])
  const featuredJobs = useMemo(() => {
    const f = (finalJobList || []).filter((j: any) => j.isFeatureDisplayed);
    // sort pinned first
    return f.sort((a: any, b: any) => (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0));
  }, [finalJobList]);

  // Order: put currently displayed featured jobs first, then the rest (parity with temping list)
  const mainListOrdered = useMemo(() => {
    const nonDisplayed = (finalJobList || []).filter((j: any) => !j.isFeatureDisplayed);
    return [...featuredJobs, ...nonDisplayed];
  }, [finalJobList, featuredJobs.length]);

  const onJobClick = (job: HygienistBackendJob | BackendJob) => {
    history.push({
      pathname: 'jobPostDetails',
      state: {
        job,
        hygienistUser
      },
    })
  }

  return (
    <Container>
      <HeaderRow>
        {onBack && (
          <BackButton onClick={onBack} className="no-fastclick" aria-label="Back">
            <ArrowBackIcon />
            <span>Back</span>
          </BackButton>
        )}
        <Title>Permanent Job Board</Title>
      </HeaderRow>
      <TitleContentContainer>
        <TitleContentButton onClick={onSortButtonClick} className='no-fastclick'>
          <StyledArrowDownwardIcon/>
          <span>{sortBy}</span>
        </TitleContentButton>
        <TitleContentButton onClick={onFilterButtonClick} className='no-fastclick'>
          <StyledFilterListIcon/>
          <span>Filter</span>
        </TitleContentButton>
      </TitleContentContainer>
      {hygienistType !== undefined && hygienistType >= 2 && <UploadResumeButton/>}
      {/* Featured strip removed: feature highlight is applied directly to full-width list cards for parity with temping */}
      {mainListOrdered.length > 0 ? (
        <JobList jobs={mainListOrdered} onJobClick={onJobClick} isHygienist={true}/>
      ) : !loading ? (
        <EmptyState>
          <EmptyStateText>No Jobs</EmptyStateText>
          <EmptyStateSubtext>There are no job postings available right now. Check back later!</EmptyStateSubtext>
        </EmptyState>
      ) : null}
    </Container>
  )
}

export default AvailablePositions;

